import { memo, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { CloseIcon, ArrowBackIcon } from 'src/themes/cosmos/icons';
import FloatingOverlay from 'src/themes/cosmos/components/FloatingOverlay';
import IconButton from 'src/themes/cosmos/components/IconButton';
import { SETTING_CONTAINERS as SETTING_CONTAINER_NAME } from 'src/interfaces/enums';
import useClickOutside, { ClickOutsideEvent } from 'src/hooks/useClickOutside';
import useUI from 'src/hooks/useUI';
import styles from './styles.module.scss';
import { withSettingsOverlayProvider } from './SettingsOverlayContext';
import useSettingsOverlay from './hooks/useSettingsOverlay';
const DELAY_TO_HIDE_SETTINGS = 100;

const SettingsOverlay = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { containerName, setContainerName, container } = useSettingsOverlay();
  const { hideSettings, settingsVisible } = useUI();

  const handleClickOutside = useCallback(
    (event: ClickOutsideEvent) => {
      if (settingsVisible) {
        event.stopImmediatePropagation();
        setTimeout(hideSettings, DELAY_TO_HIDE_SETTINGS);
      }
    },
    [settingsVisible, hideSettings],
  );

  useClickOutside(containerRef?.current, handleClickOutside);

  useEffect(() => {
    setContainerName(SETTING_CONTAINER_NAME.MAIN);
  }, [settingsVisible, setContainerName]);

  return (
    <div
      className={classNames(styles.container, { [styles.visible]: settingsVisible })}
      data-testid='settings-overlay'
      ref={containerRef}
    >
      <FloatingOverlay
        title={container.title}
        leftIcon={
          containerName !== SETTING_CONTAINER_NAME.MAIN && (
            <IconButton
              aria-label='go back'
              icon={<ArrowBackIcon />}
              className={styles.backButton}
              onClick={() => {
                setContainerName(SETTING_CONTAINER_NAME.MAIN);
              }}
            />
          )
        }
        rightIcon={
          <IconButton
            aria-label='close settings'
            icon={<CloseIcon />}
            className={styles.closeButton}
            onClick={hideSettings}
          />
        }
      >
        {container.component}
      </FloatingOverlay>
    </div>
  );
};

export default memo(withSettingsOverlayProvider(SettingsOverlay));
